'use client';

import { useEffect, useRef, useState } from 'react';

const NICEJOB_LAYOUTS = new Set(['grid', 'uniform-grid', 'single', 'slider']);

function normalizeNiceJobFilters(value) {
    const unwrappedValue =
        value &&
        typeof value === 'object' &&
        !Array.isArray(value) &&
        'value' in value
            ? value.value
            : value;
    const raw = Array.isArray(unwrappedValue)
        ? unwrappedValue
        : typeof unwrappedValue === 'string'
        ? unwrappedValue.split(/[,;\n]/)
        : [];

    return Array.from(
        new Set(raw.map((item) => String(item ?? '').trim()).filter(Boolean))
    );
}

export default function NiceJobStories({
    layout = 'grid',
    filters = [],
    className = '',
}) {
    const widgetRef = useRef(null);
    const [sliderState, setSliderState] = useState({
        hasCards: false,
        canGoBack: false,
        canGoForward: false,
        controlTop: null,
    });
    const normalizedLayout = NICEJOB_LAYOUTS.has(
        String(layout).toLowerCase().trim()
    )
        ? String(layout).toLowerCase().trim()
        : 'grid';
    const isSlider = normalizedLayout === 'slider';
    const isGrid = normalizedLayout === 'uniform-grid';
    // NiceJob supports one exact data-filter-tag value. Combined tag values
    // are ignored by the widget and cause unfiltered stories to be returned.
    const niceJobFilter = normalizeNiceJobFilters(filters)[0] || '';
    const niceJobLayout = isSlider || isGrid ? 'single' : normalizedLayout;
    const niceJobFilterKey = niceJobFilter;

    useEffect(() => {
        const widget = widgetRef.current;
        if (!widget || !isSlider) {
            setSliderState({
                hasCards: false,
                canGoBack: false,
                canGoForward: false,
                controlTop: null,
            });
            return;
        }

        let track = null;
        const updateSliderState = () => {
            const nextTrack = widget.querySelector('.nj--stories-cards--wrap');
            if (track !== nextTrack) {
                track?.removeEventListener('scroll', updateSliderState);
                track = nextTrack;
                track?.addEventListener('scroll', updateSliderState, {
                    passive: true,
                });
            }

            if (!track) {
                setSliderState({
                    hasCards: false,
                    canGoBack: false,
                    canGoForward: false,
                    controlTop: null,
                });
                return;
            }

            const maxScrollLeft = Math.max(
                0,
                track.scrollWidth - track.clientWidth
            );
            const containerRect = widget.parentElement?.getBoundingClientRect();
            const trackRect = track.getBoundingClientRect();
            const nextState = {
                hasCards: track.querySelectorAll('.nj-card--single').length > 0,
                canGoBack: track.scrollLeft > 2,
                canGoForward: track.scrollLeft < maxScrollLeft - 2,
                controlTop: containerRect
                    ? trackRect.top - containerRect.top + trackRect.height / 2
                    : null,
            };
            setSliderState((currentState) =>
                currentState.hasCards === nextState.hasCards &&
                currentState.canGoBack === nextState.canGoBack &&
                currentState.canGoForward === nextState.canGoForward &&
                Math.abs(
                    (currentState.controlTop ?? 0) - (nextState.controlTop ?? 0)
                ) < 0.5
                    ? currentState
                    : nextState
            );
        };

        const observer = new MutationObserver(updateSliderState);
        observer.observe(widget, { childList: true, subtree: true });
        const resizeObserver = new ResizeObserver(updateSliderState);
        resizeObserver.observe(widget);
        updateSliderState();

        return () => {
            observer.disconnect();
            resizeObserver.disconnect();
            track?.removeEventListener('scroll', updateSliderState);
        };
    }, [isSlider, niceJobLayout, niceJobFilterKey]);

    const scrollSlider = (direction) => {
        const track = widgetRef.current?.querySelector(
            '.nj--stories-cards--wrap'
        );
        const firstCard = track?.querySelector('.nj-card--single');
        if (!track || !firstCard) return;

        const styles = window.getComputedStyle(track);
        const gap = Number.parseFloat(styles.columnGap || styles.gap) || 0;
        const distance = firstCard.getBoundingClientRect().width + gap;
        const reduceMotion = window.matchMedia?.(
            '(prefers-reduced-motion: reduce)'
        ).matches;
        track.scrollBy({
            left: direction * distance,
            behavior: reduceMotion ? 'auto' : 'smooth',
        });
    };

    return (
        <div
            className={`${className} ${
                isSlider ? 'nicejob-stories-slider relative' : ''
            } ${isGrid ? 'nicejob-stories-grid' : ''}`}
        >
            <div
                key={`${niceJobLayout}-${niceJobFilterKey || 'all'}`}
                ref={widgetRef}
                className="nj-stories"
                data-filter-media="hide"
                data-media="hide"
                data-branding="bottom"
                {...(niceJobFilter
                    ? {
                          'data-filter-tag': niceJobFilter,
                      }
                    : {})}
                data-style={
                    niceJobLayout === 'grid' ? undefined : niceJobLayout
                }
            />
            {isSlider && sliderState.hasCards && (
                <>
                    <button
                        type="button"
                        className="nicejob-slider-control nicejob-slider-control--previous"
                        style={{ top: sliderState.controlTop ?? '50%' }}
                        onClick={() => scrollSlider(-1)}
                        disabled={!sliderState.canGoBack}
                        aria-label="Previous NiceJob reviews"
                    >
                        <i
                            className="fa-solid fa-angle-left"
                            aria-hidden="true"
                        />
                    </button>
                    <button
                        type="button"
                        className="nicejob-slider-control nicejob-slider-control--next"
                        style={{ top: sliderState.controlTop ?? '50%' }}
                        onClick={() => scrollSlider(1)}
                        disabled={!sliderState.canGoForward}
                        aria-label="Next NiceJob reviews"
                    >
                        <i
                            className="fa-solid fa-angle-right"
                            aria-hidden="true"
                        />
                    </button>
                </>
            )}
        </div>
    );
}
